import { useState, useEffect } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { ArrowLeft, Mail, Loader2 } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface SubscriptionInfo {
  id: string;
  email?: string;
  maskedEmail?: string;
  subscription_status: string;
  email_notifications_enabled: boolean;
  isOwner: boolean;
}

const PreferencesWithId = () => {
  const { subscriptionId } = useParams();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [subscriptionInfo, setSubscriptionInfo] = useState<SubscriptionInfo | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [infoLoading, setInfoLoading] = useState(true);
  const { toast } = useToast();
  const { t } = useTranslation();

  // Redirect to preferences when user is confirmed as owner
  useEffect(() => {
    if (subscriptionInfo?.isOwner) {
      console.log('PreferencesWithId - User is owner, redirecting to /preferences');
      navigate('/preferences', { 
        state: { subscriptionId },
        replace: true 
      });
    }
  }, [subscriptionInfo?.isOwner, navigate, subscriptionId]);

  useEffect(() => {
    if (subscriptionId) {
      // Wait for session token to be available before making requests
      const waitForSessionAndFetch = async () => {
        let attempts = 0;
        const maxAttempts = 5;
        const attemptDelay = 500;

        while (attempts < maxAttempts) {
          const sessionData = localStorage.getItem('newsletter_session');
          const sessionToken = sessionData ? JSON.parse(sessionData)?.session_token : null;
          console.log(`PreferencesWithId - Attempt ${attempts + 1} - Session token found:`, !!sessionToken);

          if (sessionToken) {
            // Session token found, make the request
            console.log('PreferencesWithId - Session token available, fetching subscription info');
            await fetchSubscriptionInfo(subscriptionId);
            return;
          }

          attempts++;
          
          // If it's the first attempt, try immediately without session token
          if (attempts === 1) {
            console.log('PreferencesWithId - No session token yet, trying first request');
            await fetchSubscriptionInfo(subscriptionId);
          }

          // Wait before next attempt (except on last attempt)
          if (attempts < maxAttempts) {
            await new Promise(resolve => setTimeout(resolve, attemptDelay));
          }
        }

        // Final attempt without session token if none found
        console.log('PreferencesWithId - Final attempt without session token');
        await fetchSubscriptionInfo(subscriptionId);
      };

      // Start the process with a small delay to allow localStorage sync
      setTimeout(waitForSessionAndFetch, 200);
    }
  }, [subscriptionId]);

  const fetchSubscriptionInfo = async (subscriptionId: string) => {
    setInfoLoading(true);
    
    // Clear previous state to ensure fresh render
    setSubscriptionInfo(null);
    setEmail("");
    
    try {
      const sessionData = localStorage.getItem('newsletter_session');
      const sessionToken = sessionData ? JSON.parse(sessionData)?.session_token : null;
      console.log('Fetching subscription info - Session token:', !!sessionToken, 'Subscription ID:', subscriptionId);
      
      const { data, error } = await supabase.functions.invoke('get-subscription-info', {
        body: { subscription_id: subscriptionId, session_token: sessionToken }
      });

      if (error) throw error;

      if (data?.error) {
        console.log('Subscription info error:', data.error);
        toast({
          title: t("preferencesRequest.errors.errorTitle"),
          description: data.error,
          variant: "destructive"
        });
        return;
      }

      console.log('Subscription info received:', { 
        isOwner: data?.isOwner, 
        hasEmail: !!data?.email, 
        hasMaskedEmail: !!data?.maskedEmail,
        sessionTokenPresent: !!sessionToken 
      });
      
      setSubscriptionInfo(data);
      
      if (data.isOwner && data.email) {
        setEmail(data.email);
        console.log('PreferencesWithId - User is owner, email set:', data.email);
      } else if (sessionToken && !data.isOwner) {
        console.warn('Session token exists but user is not owner - this should not happen after magic link validation');
      }

    } catch (error: any) {
      console.error('Error fetching subscription info:', error);
      toast({
        title: t("preferencesRequest.errors.errorTitle"), 
        description: t("preferencesRequest.errors.loadError"),
        variant: "destructive"
      });
    } finally {
      setInfoLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Only validate email if user is not owner and no maskedEmail (fallback case)
    if (!subscriptionInfo?.isOwner && !subscriptionInfo?.maskedEmail && (!email || !email.includes('@'))) {
      toast({
        title: t("preferencesRequest.errors.errorTitle"),
        description: t("preferencesRequest.errors.invalidEmail"),
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);

    try {
      const requestBody = subscriptionInfo?.isOwner 
        ? { 
            email: subscriptionInfo.email,
            redirect_to: '/preferences'
          }
        : subscriptionInfo
        ? {
            subscription_id: subscriptionId,
            redirect_to: '/preferences'
          }
        : { 
            email: email.toLowerCase().trim(),
            redirect_to: '/preferences'
          };

      const { data, error } = await supabase.functions.invoke('generate-magic-link', {
        body: requestBody
      });

      if (error) {
        throw error;
      }

      if (data?.error) {
        toast({
          title: t("preferencesRequest.errors.errorTitle"),
          description: data.error,
          variant: "destructive"
        });
        return;
      }

      setEmailSent(true);
      toast({
        title: t("preferencesRequest.errors.successTitle"),
        description: t("preferencesRequest.errors.successDescription")
      });

    } catch (error: any) {
      console.error('Error generating magic link:', error);
      toast({
        title: t("preferencesRequest.errors.errorTitle"),
        description: error.message || t("preferencesRequest.errors.sendError"),
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (emailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-newsletter-light via-background to-newsletter-light/50 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="h-8 w-8 text-green-600" />
            </div>
            <CardTitle className="text-2xl">{t("preferencesRequest.success.title")}</CardTitle>
            <CardDescription 
              className="text-center"
              dangerouslySetInnerHTML={{
                __html: t("preferencesRequest.success.description", { 
                  email: subscriptionInfo?.email || subscriptionInfo?.maskedEmail || email 
                })
              }}
            />
          </CardHeader>
          <CardFooter className="flex flex-col gap-4">
            <Button 
              variant="outline" 
              onClick={() => {
                setEmailSent(false);
                setEmail("");
              }}
              className="w-full"
            >
              {t("preferencesRequest.success.sendToAnother")}
            </Button>
            <Link to="/">
              <Button variant="ghost" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("preferencesRequest.success.backHome")}
              </Button>
            </Link>
          </CardFooter>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-newsletter-light via-background to-newsletter-light/50 flex items-center justify-center p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-2xl text-center">{t("preferencesRequest.access.title")}</CardTitle>
          <CardDescription className="text-center">
            {subscriptionInfo?.isOwner 
              ? t("preferencesRequest.access.ownerDescription")
              : subscriptionInfo?.maskedEmail
              ? t("preferencesRequest.access.maskedEmailDescription")
              : t("preferencesRequest.access.description")
            }
          </CardDescription>
        </CardHeader>
        
        <form onSubmit={handleSubmit}>
          <CardContent className="space-y-4">
            {/* Show fixed masked email when not owner but has maskedEmail */}
            {!subscriptionInfo?.isOwner && subscriptionInfo?.maskedEmail && (
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4" />
                  <span className="font-medium">{subscriptionInfo.maskedEmail}</span>
                </div>
              </div>
            )}
            
            {/* Show editable email field only when not owner AND no maskedEmail (fallback) */}
            {!subscriptionInfo?.isOwner && !subscriptionInfo?.maskedEmail && (
              <div className="space-y-2">
                <Label htmlFor="email">{t("preferencesRequest.access.emailLabel")}</Label>
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder={infoLoading ? t("preferencesRequest.access.loadingPlaceholder") : t("preferencesRequest.access.emailPlaceholder")}
                  required={!subscriptionInfo?.maskedEmail}
                  disabled={isLoading || infoLoading}
                />
              </div>
            )}
            
            {/* Show owner email */}
            {subscriptionInfo?.isOwner && (
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-2 text-sm">
                  <Mail className="w-4 h-4" />
                  <span className="font-medium">{subscriptionInfo.email}</span>
                </div>
              </div>
            )}
          </CardContent>
          
          <CardFooter className="flex flex-col gap-4">
            <Button 
              type="submit" 
              className="w-full" 
              disabled={isLoading || (!subscriptionInfo?.isOwner && !subscriptionInfo?.maskedEmail && !email) || infoLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("preferencesRequest.access.sendingButton")}
                </>
              ) : infoLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t("preferencesRequest.access.loadingButton")}
                </>
              ) : (
                t("preferencesRequest.access.sendButton")
              )}
            </Button>
            
            <Link to="/">
              <Button variant="ghost" className="w-full">
                <ArrowLeft className="w-4 h-4 mr-2" />
                {t("preferencesRequest.success.backHome")}
              </Button>
            </Link>
          </CardFooter>
        </form>
      </Card>
    </div>
  );
};

export default PreferencesWithId;